import { useRef, useCallback, useEffect } from 'react';

interface UseVoiceActivityOptions {
  stream: MediaStream | null;
  onSilence: () => void;
  enabled: boolean;
  silenceThreshold?: number; // ms of silence before firing (default 1500)
  volumeThreshold?: number;  // RMS below this = silence (default 0.015)
}

export function useVoiceActivity({
  stream,
  onSilence,
  enabled,
  silenceThreshold = 1500,
  volumeThreshold = 0.015,
}: UseVoiceActivityOptions) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const silentSinceRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const hasSpokenRef = useRef(false); // Must speak before silence triggers

  const pause = useCallback(() => {
    pausedRef.current = true;
    silentSinceRef.current = null;
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    hasSpokenRef.current = false; // Require speaking again after reader finishes
    silentSinceRef.current = null;
  }, []);

  useEffect(() => {
    if (!stream || !enabled) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      intervalRef.current = null;
      silentSinceRef.current = null;
      hasSpokenRef.current = false;
      return;
    }

    // Reset paused state so VAD isn't stuck from a previous recording session
    pausedRef.current = false;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    sourceRef.current = source;
    hasSpokenRef.current = false;

    const dataArray = new Float32Array(analyser.fftSize);

    intervalRef.current = window.setInterval(() => {
      if (pausedRef.current) return;

      analyser.getFloatTimeDomainData(dataArray);

      // Calculate RMS volume
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / dataArray.length);

      if (rms >= volumeThreshold) {
        // Speaking — mark that actor has spoken, reset silence timer
        hasSpokenRef.current = true;
        silentSinceRef.current = null;
      } else if (hasSpokenRef.current) {
        // Silent, but only trigger if actor has spoken first
        if (silentSinceRef.current === null) {
          silentSinceRef.current = Date.now();
        } else if (Date.now() - silentSinceRef.current >= silenceThreshold) {
          silentSinceRef.current = null;
          hasSpokenRef.current = false;
          onSilence();
        }
      }
      // If !hasSpoken and silent: do nothing (wait for actor to start)
    }, 100);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      source.disconnect();
      audioContext.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      intervalRef.current = null;
    };
  }, [stream, enabled, silenceThreshold, volumeThreshold, onSilence]);

  return { pause, resume };
}
